import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { ArrowRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import AppLogo from '@/components/generals/app-logo'
import CtaLink from '@/components/generals/cta-link'
import ThemeToggle from '@/components/generals/theme-toggle'
import { appLinks, navItems } from '@/constants/site'

const SiteHeader: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (!open) return

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', handleKey)

        return () => window.removeEventListener('keydown', handleKey)
    }, [open])

    useEffect(() => {
        const media = window.matchMedia('(min-width: 1024px)')

        const handleChange = () => {
            if (media.matches) setOpen(false)
        }

        media.addEventListener('change', handleChange)

        return () => media.removeEventListener('change', handleChange)
    }, [])

    return (
        <header className='sticky top-0 z-50 border-b border-border/70 bg-background/80 backdrop-blur-xl'>
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8 lg:h-[4.5rem]'>
                <AppLogo />

                <div className='hidden items-center gap-8 lg:flex'>
                    <nav aria-label='Main'>
                        <ul className='flex items-center gap-1'>
                            {navItems.map((item) => (
                                <li key={item.href}>
                                    <a
                                        href={item.href}
                                        className='rounded-md px-3 py-2 text-[0.92rem] font-medium text-foreground/70 transition-colors hover:text-foreground'
                                    >
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <CtaLink href={appLinks.playStore} external size='sm'>
                        Google Play
                    </CtaLink>
                </div>

                <div className='flex items-center gap-1'>
                    <CtaLink
                        href={appLinks.playStore}
                        external
                        size='sm'
                        className='mr-1 hidden sm:inline-flex lg:hidden'
                    >
                        Google Play
                    </CtaLink>

                    <ThemeToggle />

                    <button
                        type='button'
                        onClick={() => setOpen((value) => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className='grid size-10 place-items-center rounded-lg text-foreground transition-colors hover:bg-muted lg:hidden'
                    >
                        <Plus className={cn('size-6 transition-transform duration-300', open && 'rotate-45')} strokeWidth={1.8} />
                    </button>
                </div>
            </div>

            <div
                id='mobile-menu'
                hidden={!open}
                className='border-t border-border/70 bg-background lg:hidden'
            >
                <nav aria-label='Mobile' className='mx-auto max-w-7xl px-5 pt-2 sm:px-8'>
                    <ul className='divide-y divide-border/70'>
                        {navItems.map((item) => (
                            <li key={item.href}>
                                <a
                                    href={item.href}
                                    onClick={() => setOpen(false)}
                                    className='flex items-center justify-between py-4 text-base font-semibold text-foreground'
                                >
                                    {item.label}

                                    <ArrowRight className='size-4 text-muted-foreground' />
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='mx-auto max-w-7xl px-5 pb-6 pt-2 sm:px-8'>
                    <CtaLink
                        href={appLinks.playStore}
                        external
                        onClick={() => setOpen(false)}
                        className='w-full'
                    >
                        Get it on Google Play
                    </CtaLink>
                </div>
            </div>
        </header>
    )
}

export default SiteHeader
